/**
 * Draggable - Simple drag and drop functionality
 * 
 * Usage:
 * const draggable = new Draggable(element, { containment: 'window' })
 * draggable.enable()
 * 
 * Or as a mixin:
 * class MyComponent extends DraggableMixin(BaseClass) { ... }
 */
import { Position } from '@js/types'
/* __________________________________________________________________________
|                                T Y P E S                                   |
|__________________________________________________________________________ */

export interface DraggableOptions {
  // Containment
  containment?: 'window' | 'parent' | HTMLElement | null
  
  // Callbacks
  onStart?: (element: HTMLElement, position: Position) => void
  onDrag?: (element: HTMLElement, position: Position) => void
  onEnd?: (element: HTMLElement, position: Position) => void
  onClick?: (element: HTMLElement, position: Position) => void

  // Behavior
  axis?: 'x' | 'y' | 'both';          // default 'both'
  handle?: HTMLElement | string  // Drag handle (element or selector)
  cancel?: string              // elements to cancel from the draggable range
  cancelClass?: string         // elements to cancel from the draggable range
  cursor?: string  // Cursor style when draggable (default: 'grab')
  cursorDragging?: string  // Cursor style when dragging (default: 'grabbing')
  clickThreshold?: number  // Pixels to distinguish click from drag
  
  // Initial state
  disabled?: boolean

}


const DEFAULT_CONFIG: Required<Omit<DraggableOptions, 'onStart' | 'onDrag' | 'onEnd' | 'onClick' | 'handle' | 'cancel' | 'cancelClass'>> = {
  containment: 'window',
  axis: 'both',
  cursor: 'grab',
  cursorDragging: 'grabbing',
  disabled: false,
  clickThreshold: 5  // 5px movement = drag, not click
}
/* __________________________________________________________________________
|                                C L A S S                                   |
|__________________________________________________________________________ */

export class Draggable {
  private element: HTMLElement
  private options: DraggableOptions
  private isDragging: boolean = false
  private isEnabled: boolean = false
  
  // Drag state
  private startX: number = 0
  private startY: number = 0
  private startLeft: number = 0
  private startTop: number = 0
  
  // Click detection
  private hasMoved: boolean = false
  private moveDistance: number = 0
  
  // Handle element
  private handle: HTMLElement | null = null

  constructor(element: HTMLElement, options: DraggableOptions = {}) {
    this.element = element
    this.options = { ...DEFAULT_CONFIG, ...options }
    
    if (!this.options.disabled) {
      this.enable()
    }
  }

  /**
   * Enable dragging
   */
  enable(): void {
    if (this.isEnabled) return
    
    this.setupElement()
    this.setupHandle()
    this.attachListeners()
    this.isEnabled = true
  }

  /**
   * Disable dragging
   */
  disable(): void {
    if (!this.isEnabled) return
    
    this.detachListeners()
    this.isEnabled = false
    this.element.style.cursor = ''
  }

  /**
   * Destroy and clean up
   */
  destroy(): void {
    this.disable()
  }

  /**
   * Update options
   */
  updateOptions(newOptions: Partial<DraggableOptions>): void {
    this.options = { ...this.options, ...newOptions }
    
    if (this.isEnabled) {
      this.disable()
      this.enable()
    }
  }

  /**
   * Get current position
   */
  getPosition(): Position {
    return {
      left: this.element.offsetLeft,
      top: this.element.offsetTop
    }
  }

  /**
   * Set position programmatically
   */
  setPosition(position: Partial<Position>): void {
    if (position.left !== undefined) {
      this.element.style.left = `${position.left}px`
    }
    if (position.top !== undefined) {
      this.element.style.top = `${position.top}px`
    }
  }

  /**
   * Check if currently dragging
   */
  isDraggingNow(): boolean {
    return this.isDragging
  }

  /* Private methods */

  private setupElement(): void {
    const position = window.getComputedStyle(this.element).position
    if (position !== 'absolute' && position !== 'fixed' && position !== 'relative') {
      this.element.style.position = 'absolute'
    }
    
    this.element.style.cursor = this.options.cursor || 'grab'
  }

  private setupHandle(): void {
    if (this.options.handle) {
      if (typeof this.options.handle === 'string') {
        this.handle = this.element.querySelector(this.options.handle)
      } else {
        this.handle = this.options.handle
      }
    } else {
      this.handle = this.element
    }
  }

  private attachListeners(): void {
    if (this.handle) {
      this.handle.addEventListener('mousedown', this.onPointerDown)
      this.handle.addEventListener('touchstart', this.onPointerDown, { passive: false })
    }
  }

  private detachListeners(): void {
    if (this.handle) {
      this.handle.removeEventListener('mousedown', this.onPointerDown)
      this.handle.removeEventListener('touchstart', this.onPointerDown)
    }
    this.removeDocumentListeners()
  }

  private removeDocumentListeners(): void {
    document.removeEventListener('mousemove', this.onPointerMove)
    document.removeEventListener('mouseup', this.onPointerUp)
    document.removeEventListener('touchmove', this.onPointerMove)
    document.removeEventListener('touchend', this.onPointerUp)
  }

  private getPoint = (e: MouseEvent | TouchEvent): { clientX: number; clientY: number } => {
    return 'touches' in e ? e.touches[0] : e
  }

  private onPointerDown = (e: MouseEvent | TouchEvent): void => {

    const rawTarget = e.target as Node

    // Ensure we get an HTMLElement (text nodes can break closest())
    const target = rawTarget instanceof HTMLElement
      ? rawTarget
      : rawTarget.parentElement

    if (!target) return

    if (target.closest('[data-drag-cancel="true"]')) return

    // 🔥 CANCEL BY SELECTOR
    if (this.options.cancel) {
      const cancelMatch = target.closest(this.options.cancel)
      if (cancelMatch && this.element.contains(cancelMatch)) {
        return
      }
    }

    // 🔥 CANCEL BY CLASS (children included)
    if (this.options.cancelClass) {
      const cancelMatch = target.closest(`.${this.options.cancelClass}`) 
        ?? (target.querySelector(`.${this.options.cancelClass}`) ? target : null)
      if (cancelMatch && this.element.contains(cancelMatch)) {
        return
      }
    }

    // Default interactive elements safeguard
    if (
      target.matches('input, textarea, select, button') ||
      target.closest('input, textarea, select, button') ||
      target.isContentEditable
    ) {
      return
    }

    e.preventDefault()
    
    const point = this.getPoint(e)

    this.startX = point.clientX
    this.startY = point.clientY

    const rect = this.element.getBoundingClientRect()
    this.startLeft = rect.left + window.scrollX
    this.startTop = rect.top + window.scrollY

    this.isDragging = true
    this.hasMoved = false
    this.moveDistance = 0

    this.element.style.cursor = this.options.cursorDragging || 'grabbing'

    document.addEventListener('mousemove', this.onPointerMove)
    document.addEventListener('mouseup', this.onPointerUp)
    document.addEventListener('touchmove', this.onPointerMove, { passive: false })
    document.addEventListener('touchend', this.onPointerUp)

    this.options.onStart?.(this.element, this.getPosition())

    console.debug(`[Draggable ${this.element.id}] Pointer DOWN`);
  }
private onPointerMove = (e: MouseEvent | TouchEvent): void => {
    if (!this.isDragging) return;
    e.preventDefault();

    const point = this.getPoint(e);
    const dx = point.clientX - this.startX;
    const dy = point.clientY - this.startY;

    // ✅ Track movement distance and set hasMoved
    this.moveDistance = Math.sqrt(dx * dx + dy * dy);
    if (this.moveDistance > (this.options.clickThreshold ?? 5)) {
      this.hasMoved = true;
    }

    let left = this.startLeft + dx;
    let top  = this.startTop + dy;

    if (this.options.axis === 'x') {
      top = this.startTop;
    } else if (this.options.axis === 'y') {
      left = this.startLeft;
    }

    const constrained = this.applyContainment(left, top);
    left = constrained.left;
    top  = constrained.top;

    this.element.style.left = `${left}px`;
    this.element.style.top  = `${top}px`;

    if (this.hasMoved) {
      this.options.onDrag?.(this.element, { left, top });
    }
  }
  private onPointerUp = (): void => {
    if (!this.isDragging) return
    
    this.isDragging = false
    this.element.style.cursor = this.options.cursor || 'grab'
    
    this.removeDocumentListeners()
    
    const position = this.getPosition()
    
    // Distinguish between click and drag
    if (!this.hasMoved) {
      // This was a click, not a drag
      console.debug('Click detected (moved', this.moveDistance.toFixed(1), 'px)')
      this.options.onClick?.(this.element, position)
    } else {
      // This was a drag
      console.debug('Drag detected (moved', this.moveDistance.toFixed(1), 'px)')
      this.options.onEnd?.(this.element, position)
    }
  }

  private applyContainment(left: number, top: number): Position {
    const containment = this.options.containment
    
    if (containment === 'window') {
      const maxX = window.innerWidth - this.element.offsetWidth
      const maxY = window.innerHeight - this.element.offsetHeight
      left = Math.max(0, Math.min(left, maxX))
      top = Math.max(0, Math.min(top, maxY))
    } else if (containment === 'parent' && this.element.parentElement) {
      const parent = this.element.parentElement
      const maxX = parent.offsetWidth - this.element.offsetWidth
      const maxY = parent.offsetHeight - this.element.offsetHeight
      left = Math.max(0, Math.min(left, maxX))
      top = Math.max(0, Math.min(top, maxY))
    } else if (containment instanceof HTMLElement) {
      const rect = containment.getBoundingClientRect()
      const maxX = rect.width - this.element.offsetWidth
      const maxY = rect.height - this.element.offsetHeight
      left = Math.max(rect.left, Math.min(left, rect.left + maxX))
      top = Math.max(rect.top, Math.min(top, rect.top + maxY))
    }
    
    return { left, top }
  }
}
